import { faChevronDown, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { styled, TableCell, TableRow, Typography } from '@mui/material';
import { format, isValid, parseISO } from 'date-fns';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileTypeRadioGroup } from '../EditModal/FileTypeRadioGroup';
import { ProductFileData } from '../mapSrcToProductFiles';
import { FlexColumn, FlexRow, Label, OutlinedButton } from '../styles';
import { FileIcon } from '../UploadModal/FileName';

type UploadedFilesRowProps = Readonly<{
  productFile: ProductFileData;
  openModal: (productFile: ProductFileData) => void;
}>;

export function UploadedFilesRow({ productFile, openModal }: UploadedFilesRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const { t } = useTranslation('uploads');

  const handleExpandClick = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleEditClick = () => {
    openModal(productFile);
  };

  const isExpandable = productFile?.description ?? productFile.name.match(/.(xls|xlsx)$/);
  const productName = productFile?.products?.[0]?.name;
  const updatedAt = productFile?.updatedAt ?? productFile?.createdAt ?? /* istanbul ignore next */ '';
  const updatedBy = productFile?.updatedBy ?? productFile?.createdBy ?? /* istanbul ignore next */ '';
  const isValidDate = isValid(parseISO(String(updatedAt)));
  const dateString = isValidDate ? /* istanbul ignore next */ new Date(updatedAt).toISOString() : '';
  const lastModifiedDate = formatDate(dateString);
  const lastModified = updatedBy
    ? t('dateTimeUploadedBy', { dateTime: lastModifiedDate, user: updatedBy })
    : /* istanbul ignore next */ lastModifiedDate;
  const size = convertToMegabytes(productFile.size);

  return (
    <>
      <ExpandingRow data-testid="uploaded-file-row">
        <TableCell>
          {isExpandable ? (
            <ExpandIconButton
              onClick={handleExpandClick}
              icon={isExpanded ? faChevronDown : faChevronRight}
              size="lg"
              data-testid="expand-button"
            />
          ) : null}
        </TableCell>
        <TableCell>
          <Label>
            <FileIcon fileName={productFile.name} /> {productFile.name}
          </Label>
        </TableCell>
        <TableCell>{productName}</TableCell>
        <TableCell>{lastModified}</TableCell>
        <TableCell>{size}</TableCell>
        <TableCell align="right">
          <OutlinedButton onClick={handleEditClick} data-testid="uploaded-file-edit-button">
            {t('edit')}
          </OutlinedButton>
        </TableCell>
      </ExpandingRow>
      <DetailsSection isExpanded={isExpanded} productFile={productFile} />
    </>
  );
}

type DetailsSectionProps = {
  isExpanded: boolean;
  productFile: ProductFileData;
};

function DetailsSection({ isExpanded, productFile }: DetailsSectionProps) {
  if (!isExpanded) return null;

  return (
    <>
      <Description productFile={productFile} />
      <FileTypeInfo productFile={productFile} />
    </>
  );
}
/* istanbul ignore next */
function FileTypeInfo({ productFile }: { productFile: ProductFileData }) {
  const showTypeForm = productFile.name.match(/.(xls|xlsx)$/);

  // Until we support selecting upload type, we don't show the form
  return null;

  if (!showTypeForm) return null;

  return (
    <NoBordersRow>
      <TableCell />
      <TableCell colSpan={4}>
        <FlexRow>
          <FileTypeRadioGroup productFile={productFile} disabled />
        </FlexRow>
      </TableCell>
      <TableCell />
    </NoBordersRow>
  );
}

function Description({ productFile }: { productFile: ProductFileData }) {
  const { t } = useTranslation('uploads');
  /* istanbul ignore next */
  if (!productFile.description) return null;

  return (
    <NoBordersRow data-testid="uploaded-files-row-description">
      <TableCell />
      <TableCell colSpan={3}>
        <FlexRow>
          <FlexColumn sx={{ wordBreak: 'break-word' }}>
            <Typography fontWeight="bold">{t('description')}</Typography>
            <Typography> {productFile.description}</Typography>
          </FlexColumn>
        </FlexRow>
      </TableCell>
      <TableCell />
      <TableCell />
    </NoBordersRow>
  );
}

const NoBordersRow = styled(TableRow)`
  td {
    border: none;
  }
  &:last-child td {
    border-bottom: solid 1px ${({ theme }) => theme.palette.grey[300]};
  }
`;

const ExpandingRow = styled(TableRow)`
  th,
  td {
    border-bottom: none;
    border-top: solid 1px ${({ theme }) => theme.palette.grey[300]};
  }
  &:last-child td {
    border-bottom: solid 1px ${({ theme }) => theme.palette.grey[300]};
  }
`;

const ExpandIconButton = styled(FontAwesomeIcon)`
  &:hover {
    cursor: pointer;
  }
`;
function convertToMegabytes(sizeInBytes: number | string | undefined) {
  /* istanbul ignore next */
  if (!sizeInBytes) return '';
  const sizeInMegabytes = (Number(sizeInBytes) / (1024 * 1024)).toFixed(2);
  return `${sizeInMegabytes} Mb`;
}
/* istanbul ignore next */
function formatDate(isoFormat: string = '') {
  const date = parseISO(isoFormat);
  return isValid(date) ? format(date, 'dd/MM/yyyy hh:mm') : '';
}
